/**
 * Session idleness (S0.2 / DA-3336).
 *
 * "Given my session is idle 30 minutes, when I attempt a WRITE, then I am
 * re-authenticated first." Two things in that sentence decide the shape of
 * this module:
 *
 *   ON A WRITE, not on a timer. A background logout would evict an operator
 *   mid-read and discard an unsent reason — the one piece of context the
 *   audit record cannot reconstruct afterwards. Reading is not the risk; an
 *   unattended machine issuing a production write is.
 *
 *   IDLE, not elapsed. The clock restarts on activity, so someone working
 *   continuously is never interrupted.
 *
 * Pure functions over two timestamps, so the rule is testable without a
 * clock, a provider, or fake timers advancing through a rendered tree.
 *
 * This is a UX guard, not a security boundary — the token's own expiry is
 * what the API honours, and server-side enforcement is S0.6 / DA-3340. What
 * this prevents is the console cheerfully issuing a write on behalf of
 * somebody who walked away an hour ago.
 */

/** Thirty minutes, from the acceptance criterion. */
export const IDLE_LIMIT_MS = 30 * 60 * 1000;

/**
 * Has this session been idle past the limit?
 *
 * `lastActivity` is null when nothing has been recorded yet — treated as
 * IDLE, deny-by-default: if we cannot tell when someone was last active, we
 * cannot assert that they were active recently. The cost of being wrong is
 * one re-authentication.
 *
 * A clock that moved BACKWARDS (NTP correction, a laptop waking, a VM
 * resuming) yields negative elapsed time. That is not evidence of idleness,
 * and refusing a write over it would be a lockout with no visible cause — so
 * it reads as active.
 *
 * The comparison is strict, making the limit an inclusive ceiling: a write
 * landing exactly on the boundary goes through rather than being refused by
 * a millisecond of scheduling jitter.
 */
export function isIdle(lastActivity: number | null, now: number): boolean {
  if (lastActivity === null) return true;
  const elapsed = now - lastActivity;
  if (elapsed < 0) return false;
  return elapsed > IDLE_LIMIT_MS;
}

/**
 * Milliseconds remaining before this session is idle, floored at zero.
 *
 * For a warning before the limit rather than a refusal at it. Zero means
 * "idle now", which is also what a missing stamp reports.
 */
export function msUntilIdle(lastActivity: number | null, now: number): number {
  if (lastActivity === null) return 0;
  const elapsed = now - lastActivity;
  if (elapsed < 0) return IDLE_LIMIT_MS;
  return Math.max(0, IDLE_LIMIT_MS - elapsed);
}
